<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'
import { useScreenshotStore, MAX_SCATTI, MAX_SECONDI_VIDEO, registrazioneDisponibile } from '@/stores/screenshot'

/**
 * Barra della modalità "cattura schermate": visibile solo quando la modalità è attiva.
 *
 * Sta in basso, sopra ogni contenuto, ed è marcata `data-screenshot-ignore` così non finisce
 * dentro le schermate che scatta. Il flash bianco della "macchina fotografica" ha la stessa
 * marcatura (vedi flash() qui sotto).
 */

const store = useScreenshotStore()
const router = useRouter()
const toast = useToast()
const { t } = useI18n()
const anteprima = ref<string | null>(null)
const puoRegistrare = registrazioneDisponibile()

/** Messaggio leggibile da un errore qualunque: senza questo un fallimento è indiagnosticabile. */
function dettaglioErrore(e: unknown): string {
  if (e instanceof Error && e.message) {
    // L'utente che nega la condivisione genera NotAllowedError: non è un guasto.
    if (e.name === 'NotAllowedError') return t('layout.screenshot.condivisioneAnnullata', 'Condivisione dello schermo annullata.')
    return e.message
  }
  return t('layout.screenshot.erroreInatteso', 'Errore inatteso. Riprova.')
}

/** mm:ss del cronometro di registrazione. */
const tempoRegistrazione = computed(() => {
  const s = store.secondiRegistrati
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
})

async function registra(): Promise<void> {
  if (store.registrazioneInCorso) {
    store.fermaRegistrazione()
    return
  }
  if (store.pieno) {
    toast.add({ severity: 'warn', summary: t('layout.screenshot.limite', 'Limite raggiunto'),
      detail: t('layout.screenshot.limiteElementi', { n: MAX_SCATTI }, 'Puoi allegare al massimo {n} elementi per messaggio.'), life: 4000 })
    return
  }
  try {
    await store.avviaRegistrazione()
    toast.add({ severity: 'info', summary: t('layout.screenshot.regAvviata', 'Registrazione avviata'),
      detail: t('layout.screenshot.regDettaglio', { n: MAX_SECONDI_VIDEO }, 'Si ferma da sola dopo {n} secondi. Condividi "questa scheda" per registrare anche cambiando pagina.'), life: 6000 })
  } catch (e: unknown) {
    toast.add({ severity: 'error', summary: t('layout.screenshot.regNonAvviata', 'Registrazione non avviata'),
      detail: dettaglioErrore(e), life: 6000 })
  }
}

onMounted(() => {
  // Se l'utente ha ricaricato la pagina durante la cattura, la modalità riprende da dove era.
  if (!store.attivo) store.ripristina()
})

/** Effetto flash: feedback immediato che lo scatto è avvenuto. */
function flash(): void {
  const el = document.createElement('div')
  el.setAttribute('data-screenshot-ignore', 'true')
  el.style.cssText =
    'position:fixed;inset:0;background:#fff;z-index:2147483647;pointer-events:none;' +
    'transition:opacity .45s ease-out;opacity:.85'
  document.body.appendChild(el)
  requestAnimationFrame(() => { el.style.opacity = '0' })
  setTimeout(() => el.remove(), 500)
}

async function scatta(): Promise<void> {
  if (store.pieno) {
    toast.add({ severity: 'warn', summary: t('layout.screenshot.limite', 'Limite raggiunto'),
      detail: t('layout.screenshot.limiteSchermate', { n: MAX_SCATTI }, 'Puoi allegare al massimo {n} schermate per messaggio.'), life: 4000 })
    return
  }
  try {
    await store.scatta()
    flash()
    toast.add({ severity: 'success', summary: t('layout.topbar.schermataAcquisita', { n: store.numeroScatti }, 'Schermata {n} acquisita'),
      detail: t('layout.screenshot.continua', 'Continua a navigare o torna al ticket per allegarla.'), life: 2500 })
  } catch (e: unknown) {
    // Messaggio reale dell'errore: un fallimento silenzioso qui è impossibile da diagnosticare
    // (è già successo con html2canvas e i colori oklch del tema).
    toast.add({ severity: 'error', summary: t('layout.topbar.schermataNonAcquisita', 'Schermata non acquisita'),
      detail: dettaglioErrore(e), life: 6000 })
  }
}

async function torna(): Promise<void> {
  const dest = store.ritorno
  if (!dest) { store.annulla(); return }
  // Gli scatti restano nello store: li consuma la pagina del ticket appena montata.
  await router.push({ name: dest.name, params: dest.params })
}

function annulla(): void {
  store.annulla()
  toast.add({ severity: 'info', summary: t('layout.screenshot.annullata', 'Cattura annullata'), life: 2500 })
}
</script>

<template>
  <div
    v-if="store.attivo"
    data-screenshot-ignore="true"
    class="screenshot-bar"
  >
    <div class="flex items-center gap-2 flex-wrap justify-center">
      <span class="text-sm font-medium">
        <i class="pi pi-camera mr-1" />
        {{ t('layout.screenshot.modalita', 'Modalità cattura') }}
        <span class="opacity-80">— {{ store.numeroScatti }}/{{ MAX_SCATTI }}</span>
      </span>

      <Button
        v-tooltip.top="puoRegistrare
          ? t('layout.screenshot.primoScatto', 'Al primo scatto il browser chiede di condividere la scheda: concesso una volta, gli scatti successivi sono immediati')
          : t('layout.screenshot.scattaPagina', 'Scatta una schermata di questa pagina')"
        :label="t('layout.screenshot.scatta', 'Scatta schermata')"
        icon="pi pi-camera"
        size="small"
        :loading="store.inCorso"
        :disabled="store.pieno"
        @click="scatta"
      />

      <!-- Registrazione schermo: nascosta se il browser non può (serve HTTPS) -->
      <Button
        v-if="puoRegistrare"
        :label="store.registrazioneInCorso ? t('layout.screenshot.ferma', { t: tempoRegistrazione }, 'Ferma ({t})') : t('layout.screenshot.registra', 'Registra schermo')"
        :icon="store.registrazioneInCorso ? 'pi pi-stop-circle' : 'pi pi-video'"
        :severity="store.registrazioneInCorso ? 'danger' : 'secondary'"
        size="small"
        :disabled="store.pieno && !store.registrazioneInCorso"
        @click="registra"
      />

      <!-- Video acquisiti: durata + rimozione (non hanno miniatura) -->
      <span
        v-for="(v, i) in store.video"
        :key="`v-${i}`"
        class="screenshot-video-chip"
      >
        <i class="pi pi-video" />
        {{ v.secondi }}s
        <button
          type="button"
          class="screenshot-thumb-del"
          :title="t('layout.screenshot.rimuoviReg', 'Rimuovi questa registrazione')"
          @click="store.rimuoviVideo(i)"
        >×</button>
      </span>

      <!-- Miniature: si passa sopra per vedere l'anteprima, la X rimuove lo scatto -->
      <span
        v-for="(s, i) in store.scatti"
        :key="i"
        class="screenshot-thumb"
      >
        <img
          :src="s"
          :alt="t('layout.screenshot.anteprima', 'Anteprima schermata')"
          @click="anteprima = s"
        >
        <button
          type="button"
          class="screenshot-thumb-del"
          :title="t('layout.screenshot.rimuoviSchermata', 'Rimuovi questa schermata')"
          @click="store.rimuovi(i)"
        >×</button>
      </span>

      <Button
        :label="store.ritorno?.etichetta ?? t('layout.screenshot.tornaTicket', 'Torna al ticket')"
        icon="pi pi-arrow-left"
        severity="secondary"
        size="small"
        :badge="store.numeroScatti ? String(store.numeroScatti) : undefined"
        @click="torna"
      />
      <Button
        :label="t('common.annulla', 'Annulla')"
        icon="pi pi-times"
        severity="secondary"
        text
        size="small"
        @click="annulla"
      />
    </div>
  </div>

  <!-- Anteprima a schermo pieno di uno scatto -->
  <div
    v-if="anteprima"
    data-screenshot-ignore="true"
    class="screenshot-preview"
    @click="anteprima = null"
  >
    <img
      :src="anteprima"
      :alt="t('layout.screenshot.acquisita', 'Schermata acquisita')"
    >
  </div>
</template>

<style scoped>
.screenshot-bar {
  position: fixed;
  left: 50%;
  bottom: 1rem;
  transform: translateX(-50%);
  z-index: 2000;
  max-width: min(96vw, 62rem);
  padding: 0.6rem 0.9rem;
  border-radius: 999px;
  background: var(--p-content-background);
  border: 2px solid var(--p-primary-color);
  box-shadow: 0 8px 24px rgb(0 0 0 / 25%);
  color: var(--p-text-color);
}

.screenshot-thumb {
  position: relative;
  display: inline-flex;
}

.screenshot-thumb img {
  width: 2.5rem;
  height: 1.8rem;
  object-fit: cover;
  border: 1px solid var(--p-content-border-color);
  border-radius: 4px;
  cursor: zoom-in;
}

.screenshot-thumb-del {
  position: absolute;
  top: -0.4rem;
  right: -0.4rem;
  width: 1.05rem;
  height: 1.05rem;
  line-height: 1;
  border: none;
  border-radius: 50%;
  background: var(--p-red-500, #ef4444);
  color: #fff;
  font-size: 0.8rem;
  cursor: pointer;
}

.screenshot-video-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.15rem 0.5rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: 999px;
  font-size: 0.75rem;
}

.screenshot-preview {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: rgb(0 0 0 / 70%);
  cursor: zoom-out;
}

.screenshot-preview img {
  max-width: 100%;
  max-height: 100%;
  box-shadow: 0 8px 32px rgb(0 0 0 / 50%);
}
</style>
